import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useLanguage } from "../context/LanguageContext";
import { usePageMeta } from "../utils/usePageMeta";

const PrivacyPage = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();

    // Legal pages exist in Ukrainian only, so they get a canonical but no hreflang pair
    usePageMeta({
        title: "Політика конфіденційності — SmarTOOL",
        description: "Як SmarTOOL збирає, використовує та захищає персональні дані клієнтів сервісу оренди інструменту.",
        canonical: "https://kh-tools.com/privacy",
        lang: "uk",
    });

    return (
        <div className="min-h-screen bg-gray-50">
            <Header
                title={t("privacy.title")}
                showBack
                onBack={() => navigate(-1)}
            />

            <div className="p-4 pb-8 animate-slide-in">
                {/* Title */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h1 className="text-base font-bold text-gray-900 text-center leading-snug">
                        ПОЛІТИКА КОНФІДЕНЦІЙНОСТІ ТА ЗАХИСТУ ПЕРСОНАЛЬНИХ ДАНИХ
                    </h1>
                    <p className="text-sm text-gray-600 mt-3 leading-relaxed">
                        Ця Політика визначає порядок обробки персональних даних
                        Орендарів (далі — «Клієнти») ФОП Ляшенко Є.С. (далі —
                        «Орендодавець») при наданні послуг з оренди інструменту
                        через автоматизовані пункти видачі (автомати).
                    </p>
                </div>

                {/* Section 1 */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            1
                        </span>
                        ЗАГАЛЬНІ ПОЛОЖЕННЯ
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <p>
                            <span className="font-medium text-gray-700">
                                1.1.
                            </span>{" "}
                            Використовуючи сервіс автоматизованої оренди, Клієнт
                            надає згоду на обробку його персональних даних
                            відповідно до цієї Політики та Договору публічної
                            оферти.
                        </p>
                        <p>
                            <span className="font-medium text-gray-700">
                                1.2.
                            </span>{" "}
                            Обробка даних здійснюється з метою виконання
                            договірних зобов'язань, ідентифікації Клієнта та
                            захисту майна Орендодавця.
                        </p>
                    </div>
                </div>

                {/* Section 2 */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            2
                        </span>
                        СКЛАД ДАНИХ, ЩО ОБРОБЛЯЮТЬСЯ
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <p>
                            Орендодавець обробляє наступні дані:
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Пакет пасспортних данних Клієнта.</li>
                            <li>Номер мобільного телефону.</li>
                            <li>
                                Унікальний ідентифікатор банківської картки
                                (токен), що не дозволяє отримати доступ до
                                повних даних картки.
                            </li>
                            <li>
                                Дані відеофіксації з камер, встановлених на
                                автоматизованих пунктах видачі (з метою безпеки
                                та запобігання крадіжкам).
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Section 3 */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            3
                        </span>
                        ОБМЕЖЕННЯ ВІДПОВІДАЛЬНОСТІ ОРЕНДОДАВЦЯ
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
                            <p className="font-medium text-gray-700">
                                3.1. У МЕЖАХ, ДОЗВОЛЕНИХ ЧИННИМ ЗАКОНОДАВСТВОМ
                                УКРАЇНИ, ОРЕНДОДАВЕЦЬ НЕ НЕСЕ ВІДПОВІДАЛЬНОСТІ
                                ЗА:
                            </p>
                            <ol className="list-decimal pl-5 space-y-1 font-medium text-gray-700">
                                <li>
                                    Будь-які прямі, непрямі або випадкові
                                    збитки, що виникли внаслідок використання
                                    або неможливості використання послуг
                                    сервісу.
                                </li>
                                <li>
                                    Несанкціонований доступ третіх осіб до
                                    даних Клієнта, якщо такий доступ став
                                    можливим не з вини Орендодавця (наприклад,
                                    злам пристрою Клієнта).
                                </li>
                                <li>
                                    Технічні збої в роботі банківських установ
                                    або платіжних систем, через які не було
                                    проведено або було помилково проведено
                                    платіж.
                                </li>
                                <li>
                                    Використання Клієнтом інструменту з
                                    порушенням техніки безпеки, що призвело до
                                    шкоди здоров'ю або майну Клієнта чи третіх
                                    осіб.
                                </li>
                            </ol>
                        </div>
                        <p>
                            <span className="font-medium text-gray-700">
                                3.2.
                            </span>{" "}
                            Клієнт самостійно несе відповідальність за безпеку
                            свого номера телефону та пристроїв, через які
                            здійснюється доступ до сервісу.
                        </p>
                    </div>
                </div>

                {/* Section 4 */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            4
                        </span>
                        ПЕРЕДАЧА ДАНИХ ТРЕТІМ ОСОБАМ
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <p>
                            <span className="font-medium text-gray-700">
                                4.1.
                            </span>{" "}
                            Орендодавець не передає персональні дані третім
                            особам, крім випадків:
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>
                                На вимогу правоохоронних органів у межах
                                кримінальних проваджень (наприклад, у разі
                                крадіжки інструменту).
                            </li>
                            <li>
                                Залучення банків-еквайрів та платіжних систем
                                для обробки транзакцій (дані передаються лише в
                                обсязі, необхідному для проведення платежу).
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Section 5 */}
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            5
                        </span>
                        ЗМІНИ ДО ПОЛІТИКИ
                    </h2>
                    <div className="text-sm text-gray-600 leading-relaxed space-y-2 pl-8">
                        <p>
                            <span className="font-medium text-gray-700">
                                5.1.
                            </span>{" "}
                            Орендодавець має право вносити зміни до цієї
                            Політики в односторонньому порядку. Зміни набувають
                            чинності з моменту їх публікації за цією адресою.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPage;
